import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';

import type { Template } from '@/modules/templates/types/templates.types';
import { useTranslation } from 'react-i18next';

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
  onConfirm: () => Promise<void>;
}

export function DeleteDialog({ open, onOpenChange, template, onConfirm }: DeleteDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[95vw] sm:max-w-2xl max-h-[90dvh] overflow-hidden flex flex-col p-4 sm:p-6 rounded-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="size-5 text-destructive" />
            </div>
            <AlertDialogTitle>{t('ADMIN_TEMPLATES_DELETEDIALOG_TITLE')}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            {t('ADMIN_TEMPLATES_DELETEDIALOG_DESCRIPTION', {
              title: template?.title ?? '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-row gap-2 justify-end">
          <AlertDialogCancel disabled={isDeleting} className="w-auto">
            {t('ADMIN_TEMPLATES_DELETEDIALOG_CANCEL')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className="bg-destructive text-white hover:bg-destructive/90 w-auto"
          >
            {isDeleting && <Loader2 className="size-4 animate-spin mr-2" />}
            {t('ADMIN_TEMPLATES_DELETEDIALOG_CONFIRM')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
